<template>
    <v-card class="article-card" elevation="0">
        <!-- Header with SKU and chips -->
        <div 
            class="article-header"
            :class="{ 
                'article-missing': !article.status || article.status === 'missing',
                'article-multicart': article.shelf && article.status !== 'missing'
             }"
        >
            <div class="article-sku">
                {{ article.sku }}
            </div>
            <div class="article-chips">
                <!-- Quantity moved to image overlay -->
            </div>
        </div>

        <!-- Product Image Area - only show if image exists -->
        <div v-if="getImageUrl(article)" class="article-image" :class="{ 'dark-mode': isDarkBackground }">
            <v-img
                @click="toggleBackgroundMode"
                :src="getImageUrl(article)"
                class="product-image"
                contain
                height="100%"
            >
                <template v-slot:placeholder>
                    <div class="image-placeholder">
                        <v-icon size="48" color="grey">mdi-image</v-icon>
                    </div>
                </template>
            </v-img>
            <!-- Modern Toggle Switch -->
            <div class="modern-toggle" @click="toggleBackgroundMode">
                <div class="toggle-track" :class="{ 'active': isDarkBackground }">
                    <div class="toggle-thumb" :class="{ 'active': isDarkBackground }">
                        <v-icon size="14" :color="isDarkBackground ? '#333' : '#666'">
                            {{ isDarkBackground ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}
                        </v-icon>
                    </div>
                </div>
            </div>
            
            <!-- Quantity Badge - positioned absolutely on top left -->
            <div v-if="article.qty || article.vqty" class="qty-badge" :class="{ 'qty-high': (article.qty) > 1, 'qty-low': (article.qty) <= 1 }">
                {{ article.qty || article.vqty }}x
            </div>
           
            
            <!-- Shelf Badge - positioned absolutely on top right -->
            <div v-if="article.shelf" class="shelf-badge">
                {{ article.shelf.slot }}
            </div>
            
            <!-- Reset Button - positioned absolutely on bottom right -->
            <v-btn 
                v-if="isPartOfPacklist"
                color="error" 
                @click="resetArticle"
                class="reset-button-absolute"
                elevation="3"
            >
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </div>

        <!-- Quantity Badge for cards without images -->
        <div v-if="!getImageUrl(article) && (article.qty || article.vqty)" class="qty-badge-no-image" :class="{ 'qty-high': (article.qty || article.vqty) > 1 }">
            {{ article.qty || article.vqty }}x
        </div>
        
        <!-- Shelf Badge for cards without images -->
        <div v-if="!getImageUrl(article) && article.shelf" class="shelf-badge-no-image">
            {{ article.shelf.slot }}
        </div>
        
        <!-- Reset Button for cards without images -->
        <div v-if="!getImageUrl(article) && isPartOfPacklist" class="reset-button-no-image">
            <v-btn 
                color="error" 
                @click="resetArticle"
                fab
                small
                elevation="3"
            >
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </div>

        <!-- Bottom text lines -->
        <div class="article-details" v-if="getFields(article).length > 0">
            <div class="article-line" 
                v-for="(field, i) in getFields(article)"
                :key="i"
            >
                <span class="line-label">{{ field.key }}:</span>
                <span class="line-text">{{ field.value || 'N/A' }}</span>
            </div>
        </div>
    </v-card>
</template>

<script>
export default {
    name: 'ArticleCard',
    props: {
        article: {
            type: Object,
            required: true
        },
        packlistItems: {
            type: Array,
            required: true
        },
        layout: {
            type: String,
            default: 'list'
        },
        isDarkBackground: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isPartOfPacklist() {
            return this.packlistItems.some(item => item.sku === this.article.sku);
        },
        imageHeight() {
            // Responsive image height: 300px on desktop, 500px on tablet
            return window.innerWidth >= 1200 ? 300 : 400;
        },
    },
    methods: {
        getImageUrl(product) {
            let hasDesignId = product.designId || product.attributes.designId;
            let hasPreview = product.attributes.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                return hasPreview[0].url || hasPreview;
            } else {
                let foundImageUrl = null;
                for (const [key, value] of Object.entries(product.attributes)) {
                    if (typeof value === 'string' && value.startsWith('http')) {
                        foundImageUrl = value;
                        break;
                    }
                }

                return foundImageUrl;
            }
        },
        getFields(product) {
            if (!product.translated) return [];

            let blacklist = ['_', 'preview', 'designId', 'vQty', 'qty', 'grossPrice', 'discount'];

            return Object.entries(product.attributes)
                .filter(([key, value]) => {
                    // Check if value is not an HTTP URL and not null
                    const isValidValue = !value?.startsWith?.('http') && value !== null;
                    
                    // Check if key doesn't contain any blacklisted substrings
                    const isKeyAllowed = !blacklist.some(blockedStr => 
                        key.toLowerCase().includes(blockedStr.toLowerCase())
                    );
                    
                    return isValidValue && isKeyAllowed;
                })
                .map(([key, value]) => ({
                    key,
                    value
                }));
        },
        resetArticle() {
            this.$emit('reset-article', this.article);
        },
        toggleBackgroundMode() {
            this.$emit('toggle-background-mode');
        }
    }
}
</script>

<style scoped>
.article-card {
    width: 100%;
    max-width: 100%;
    margin: 0;
    box-sizing: border-box;
    overflow: visible; /* Allow content to show properly */
    display: flex;
    flex-direction: column;
    height: auto; /* Let card height be determined by content */
    position: relative; /* Required for absolute positioning of reset button */
    
    /* Timer-like outline styling */
    border: 1px solid #e0e0e0 !important;
    border-radius: 12px !important;
    background-color: #f0f0f0 !important;
}

/* Grid layout specific adjustments - let cards grow naturally with their content */
.packlist-body.grid-layout .article-card {
    height: auto;
}

.article-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #f9f9f9;
    flex-shrink: 0; /* Header maintains its size */
    min-height: 60px; /* Consistent header height */
    background-color: #1fbc37;
}

.article-header.article-missing {
    background-color: #f44336;
    color: #f9f9f9;
}

.article-header.article-multicart {
    background-color: #8700cb;
    color: #f9f9f9;
}

.article-sku {
    font-size: clamp(14px, 3vw, 24px);
    font-weight: 600;
    color: #f9f9f9;
    background-color: #121212;
    padding: 6px 12px;
}

.article-chips {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Quantity Badge - positioned on top left of image, styled like shelf badge */
.qty-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #f44336;
    color: white;
    font-size: 72px;
    font-weight: 600;
    padding: 16px 20px;
    border: 1px solid #e0e0e0;
    z-index: 10;
    animation: blinkRed 2s ease-in-out infinite;
}

.qty-badge.qty-high {
    background-color: #d32f2f;
    color: white;
    animation: blinkRed 1.5s ease-in-out infinite; /* Faster blink for high quantities */
}
.qty-badge.qty-low {
    background-color: #121212;
    color: white;
    font-size: 16px;
    animation: none;
}

/* Quantity Badge for cards without images */
.qty-badge-no-image {
    background-color: #f44336;
    color: white;
    font-size: 72px;
    font-weight: 600;
    padding: 16px 20px;
    border: 1px solid #e0e0e0;
    text-align: center;
    margin: 16px;
    flex-shrink: 0;
    width: fit-content;
    align-self: flex-start;
    animation: blinkRed 2s ease-in-out infinite;
}

.qty-badge-no-image.qty-high {
    background-color: #d32f2f;
    color: white;
    animation: blinkRed 1.5s ease-in-out infinite; /* Faster blink for high quantities */
}

.shelf-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: #333;
    color: white;
    font-size: 72px;
    font-weight: 600;
    padding: 16px 20px;
    border: 1px solid #e0e0e0;
    z-index: 10;
}

.shelf-badge-no-image {
    background-color: #333;
    color: white;
    font-size: 72px;
    font-weight: 600;
    padding: 16px 20px;
    border: 1px solid #e0e0e0;
    text-align: center;
    margin: 16px;
    flex-shrink: 0;
}

/* Reset Button - Absolutely positioned on bottom right of image */
.reset-button-absolute {
    position: absolute !important;
    bottom: 12px;
    right: 12px;
    z-index: 3;
    background-color: #f44336 !important;
    color: white !important;
    min-width: 48px !important;
    width: 48px !important;
    height: 48px !important;
    border: 1px solid #e0e0e0 !important;
}

.reset-button-absolute:hover {
    background-color: #d32f2f !important;
    transform: scale(1.05);
    transition: all 0.2s ease;
}

.reset-button-absolute:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
}

/* Reset Button for cards without images - positioned absolutely on card */
.reset-button-no-image {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 3;
}

.reset-button-no-image .v-btn {
    background-color: #f44336 !important;
    color: white !important;
    min-width: 48px !important;
    width: 48px !important;
    height: 48px !important;
    border: 1px solid #e0e0e0 !important;
}

.reset-button-no-image .v-btn:hover {
    background-color: #d32f2f !important;
    transform: scale(1.05);
    transition: all 0.2s ease;
}

.article-image {
    position: relative;
    background: #f8f8f8;
    width: 100%;
    overflow: hidden;
    flex-shrink: 0; /* Don't let image shrink */
    height: 400px; /* Fixed height for consistency */
    display: flex;
    flex-direction: column;
}

.product-image {
    background-color: #888;
    width: 100%;
    max-width: 100%;
    height: 100%; /* Fill the image container */
    object-fit: contain; /* Maintain aspect ratio */
    border-radius: 0 !important;
}



.image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 500px;
    background-color: #888;
}

.article-details {
    padding: 12px 16px;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    justify-content: flex-start; /* Always start from top */
    gap: 8px; /* Consistent gap between properties */
    width: 100%;
    box-sizing: border-box;
    overflow: visible; /* Allow overflow to show all properties */
    flex-shrink: 0; /* Don't let details section shrink */
}

.article-line {
    display: flex;
    margin-bottom: 4px;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
    flex-wrap: wrap;
    align-items: flex-start;
}

.line-label {
    font-weight: 600;
    font-size: 24px;
    color: #333;
    margin-right: 8px;
    min-width: 45px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    flex-shrink: 0;
}

.line-text {
    color: #333;
    font-size: 24px;
    font-weight: 500;
    word-wrap: break-word;
    overflow-wrap: break-word;
    flex: 1;
    min-width: 0;
}

.article-line:last-child {
    margin-bottom: 0;
}

/* Tablet-specific reset button adjustments */
@media (min-width: 768px) and (max-width: 1199px) {
    .reset-button-absolute,
    .reset-button-no-image .v-btn {
        min-width: 44px !important;
        width: 44px !important;
        height: 44px !important;
    }
}

/* Large tablet adjustments */
@media (min-width: 901px) and (max-width: 1199px) {
    .reset-button-absolute,
    .reset-button-no-image .v-btn {
        min-width: 52px !important;
        width: 52px !important;
        height: 52px !important;
    }
}

/* Desktop adjustments - smaller cards */
@media (min-width: 1200px) {
    .article-image {
        height: 400px; /* Fixed image height consistent across devices */
    }
    
    .image-placeholder {
        height: 400px;
    }
    
    .article-details {
        padding: 8px 12px;
        gap: 6px;
    }
    
    .line-label,
    .line-text {
        font-size: 16px;
    }
    
    .shelf-badge {
        font-size: 48px; /* Smaller shelf badge on desktop */
        padding: 8px 12px;
        top: 6px;
        right: 6px;
    }
    
    .shelf-badge-no-image {
        font-size: 48px;
        padding: 8px 12px;
        margin: 8px;
    }
    
    .qty-badge {
        font-size: 48px; /* Smaller qty badge on desktop */
        padding: 8px 12px;
        top: 6px;
        left: 6px;
    }
    
    .qty-badge-no-image {
        font-size: 48px;
        padding: 8px 12px;
        margin: 8px;
    }
}

/* Dark Mode Styles - Only for Image Container */
.article-image.dark-mode {
    background: #191919 !important;
}

.article-image.dark-mode * {
    background: #191919 !important;
}

.article-image.dark-mode .product-image {
    background: #191919 !important;
    background-color: #191919 !important;
}

.article-image.dark-mode .image-placeholder {
    background: #191919 !important;
    background-color: #191919 !important;
}

.article-image.dark-mode .v-image {
    background: #191919 !important;
    background-color: #191919 !important;
}

.article-image.dark-mode .v-image__image {
    background: #191919 !important;
    background-color: #191919 !important;
}

.article-image.dark-mode .v-responsive__content {
    background: #191919 !important;
    background-color: #191919 !important;
}

/* Modern Toggle Switch */
.modern-toggle {
    position: absolute;
    bottom: 8px;
    left: 8px;
    z-index: 10;
    cursor: pointer;
    user-select: none;
}

.toggle-track {
    width: 44px;
    height: 24px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-track.active {
    background: linear-gradient(45deg, #ff9800, #ffc107);
    box-shadow: inset 0 2px 4px rgba(255, 152, 0, 0.3), 0 0 8px rgba(255, 152, 0, 0.4);
}

.toggle-thumb {
    width: 20px;
    height: 20px;
    background: linear-gradient(145deg, #ffffff, #f0f0f0);
    border-radius: 50%;
    position: absolute;
    top: 2px;
    left: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.1);
    transform: translateX(0);
}

.toggle-thumb.active {
    transform: translateX(20px);
    background: linear-gradient(145deg, #fff3e0, #ffe0b2);
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4), 0 1px 3px rgba(255, 152, 0, 0.2);
}

.modern-toggle:hover .toggle-track {
    transform: scale(1.05);
}

.modern-toggle:active .toggle-track {
    transform: scale(0.98);
}

/* Subtle glow effect for better visibility */
.modern-toggle {
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
}

.toggle-track.active {
    filter: drop-shadow(0 0 6px rgba(255, 152, 0, 0.5));
}

/* Ensure v-img fills container properly */
.article-image .v-image {
    height: 100% !important;
    width: 100% !important;
    border-radius: 0 !important;
}

.article-image .v-responsive {
    height: 100% !important;
    border-radius: 0 !important;
}

.article-image .v-image__image {
    border-radius: 0 !important;
}

.article-image .v-responsive__content {
    border-radius: 0 !important;
}

/* Red blinking animation for quantity badges */
@keyframes blinkRed {
    0% { 
        background-color: #f44336;
        box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }
    50% { 
        background-color: #d32f2f;
        box-shadow: 0 0 15px rgba(244, 67, 54, 0.8), 0 0 25px rgba(244, 67, 54, 0.6);
    }
    100% { 
        background-color: #f44336;
        box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }
}
</style>